'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import useCarouselAnimation from '@src/hooks/useCarouselAnimation';
import useCarouselIndicatorAnimation from '@src/hooks/useCarouselIndicatorAnimation';

import Container from '@src/components/Shared/Container';
import CarouselContainer from '@src/components/Shared/CarouselContainer';
import CarouselIndicators from '@src/components/Shared/CarouselIndicators';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import NextImage from '@src/components/Shared/NextImage';

import Chevron from '@src/components/Icons/Chevron';

import InspectorWrapper from '@src/components/Shared/InspectorWrapper';

import { TestimonialsPropTypes } from './Testimonials.types';

type QuotePos = {
  left: number;
  top: number;
  ready: boolean;
};

const SVG_H = 52;
const MOBILE_QUOTE_H = 20;
const DESKTOP_BREAKPOINT = 1024;

/**
 * Find the first non-empty text node inside a root element.
 */
const findFirstTextNode = (root: HTMLElement): Text | null => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      const txt = n?.textContent ?? '';
      return txt.trim().length ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    },
  });

  const first = walker.nextNode();
  return first ? (walker.currentNode as Text) : null;
};

/**
 * Find the last non-empty text node inside a root element.
 * This works across nested spans/strong/em/a/etc because it walks actual Text nodes.
 */
const findLastTextNode = (root: HTMLElement): Text | null => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      const txt = n?.textContent ?? '';
      return txt.trim().length ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    },
  });

  let last: Text | null = null;
  while (walker.nextNode()) {
    last = walker.currentNode as Text;
  }
  return last;
};

const Testimonials: React.FC<TestimonialsPropTypes> = ({ attr, id, testimonials, className }) => {
  const {
    carouselRef,
    containerRef,
    handleAddCarouselItemsToRef,
    activeIndex,
    handleShowNextItem,
    handleShowPreviousItem,
    handleJumpToSlide,
    maxIndex,
  } = useCarouselAnimation({ overrideMaxIndex: testimonials?.length - 1 });

  const { handleAddIndicatorRef } = useCarouselIndicatorAnimation({});

  // Refs for positioning quotes relative to the body copy content
  const quoteWrapRef = useRef<HTMLDivElement | null>(null);
  const bodyCopyRootRef = useRef<HTMLDivElement | null>(null);

  const [startQuotePos, setStartQuotePos] = useState<QuotePos>({
    left: 0,
    top: 0,
    ready: false,
  });

  const [endQuotePos, setEndQuotePos] = useState<QuotePos>({
    left: 0,
    top: 0,
    ready: false,
  });

  const [isDesktopViewport, setIsDesktopViewport] = useState(false);

  useEffect(() => {
    const handleViewportChange = () => {
      setIsDesktopViewport(window.innerWidth >= DESKTOP_BREAKPOINT);
    };

    handleViewportChange();
    window.addEventListener('resize', handleViewportChange);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
    };
  }, []);

  /**
   * Only block carousel drag propagation on desktop,
   * where we want text to be selectable.
   */
  const handleSelectableContentInteraction = useCallback(
    (e: React.MouseEvent<HTMLElement> | React.PointerEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
      if (!isDesktopViewport) return;

      e.stopPropagation();
    },
    [isDesktopViewport],
  );

  const desktopSelectableStyle = isDesktopViewport
    ? {
        userSelect: 'text' as const,
        WebkitUserSelect: 'text' as const,
      }
    : undefined;

  const desktopInteractionProps = isDesktopViewport
    ? {
        onMouseDownCapture: handleSelectableContentInteraction,
        onPointerDownCapture: handleSelectableContentInteraction,
        onTouchStartCapture: handleSelectableContentInteraction,
      }
    : {};

  const measureQuotes = useCallback(() => {
    const wrap = quoteWrapRef.current;
    const root = bodyCopyRootRef.current;

    if (!wrap || !root) {
      setStartQuotePos((p) => ({ ...p, ready: false }));
      setEndQuotePos((p) => ({ ...p, ready: false }));
      return;
    }

    const wrapRect = wrap.getBoundingClientRect();

    const isMobile = window.innerWidth < 768;
    const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
    const isTabletOrBelow = window.innerWidth < 1024;

    /**
     * START QUOTE
     */
    const firstText = findFirstTextNode(root);
    if (firstText) {
      const startRange = document.createRange();
      startRange.selectNodeContents(firstText);

      const startRects = startRange.getClientRects();
      const firstRect = startRects[0];

      if (firstRect) {
        const startXOffset = isTabletOrBelow ? -10 : -84;
        const startYOffset = isTabletOrBelow ? -5 : -26;

        const left = firstRect.left - wrapRect.left + startXOffset;
        const top = firstRect.top - wrapRect.top + startYOffset;

        setStartQuotePos({
          left,
          top,
          ready: true,
        });
      } else {
        setStartQuotePos((p) => ({ ...p, ready: false }));
      }
    } else {
      setStartQuotePos((p) => ({ ...p, ready: false }));
    }

    /**
     * END QUOTE
     */
    const lastText = findLastTextNode(root);
    if (lastText) {
      const endRange = document.createRange();
      endRange.selectNodeContents(lastText);

      const endRects = endRange.getClientRects();
      const lastRect = endRects[endRects.length - 1];

      if (lastRect) {
        const endXOffset = isTabletOrBelow ? 0 : 20;
        const endYOffset = isTablet ? -15 : isMobile ? -2 : 20;
        const quoteHeight = isTabletOrBelow ? MOBILE_QUOTE_H : SVG_H;

        const left = lastRect.right - wrapRect.left + endXOffset;
        const top = lastRect.bottom - wrapRect.top - quoteHeight + endYOffset;

        setEndQuotePos({
          left,
          top,
          ready: true,
        });
      } else {
        setEndQuotePos((p) => ({ ...p, ready: false }));
      }
    } else {
      setEndQuotePos((p) => ({ ...p, ready: false }));
    }
  }, []);

  useEffect(() => {
    measureQuotes();

    const raf1 = requestAnimationFrame(() => {
      measureQuotes();
    });

    const raf2 = requestAnimationFrame(() => {
      requestAnimationFrame(() => measureQuotes());
    });

    const onResize = () => measureQuotes();
    window.addEventListener('resize', onResize);

    const ro = new ResizeObserver(() => measureQuotes());
    if (quoteWrapRef.current) ro.observe(quoteWrapRef.current);
    if (bodyCopyRootRef.current) ro.observe(bodyCopyRootRef.current);

    const anyDoc = document as any;
    if (anyDoc?.fonts?.ready?.then) {
      anyDoc.fonts.ready.then(() => measureQuotes());
    }

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
    };
  }, [activeIndex, measureQuotes]);

  return (
    <>
      <Container
        attr={attr}
        containerRef={containerRef}
        id={id}
        className={`relative flex flex-col overflow-visible! rounded-[0.6rem] py-[unset] ${className}`}
        background='bg-background-tertiary'
      >
        {testimonials && testimonials.length > 1 && (
          <div className='md:space-between absolute top-1/2 left-1/2 z-1000 hidden w-[95%] -translate-x-1/2 -translate-y-1/2 transform lg:flex'>
            <button
              aria-label='arrow'
              onClick={handleShowPreviousItem}
              disabled={activeIndex <= 0}
              className={`mr-[1.6rem] flex h-[5.5rem] w-[5.5rem] rotate-180 items-center justify-center rounded-full ${
                activeIndex <= 0 ? 'cursor-not-allowed' : ''
              }`}
            >
              <Chevron className='' />
            </button>

            <button
              aria-label='arrow'
              onClick={handleShowNextItem}
              disabled={activeIndex >= testimonials?.length - 1}
              className={`ml-auto flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-full ${
                activeIndex >= testimonials?.length - 1 ? 'cursor-not-allowed' : ''
              }`}
            >
              <Chevron className='' />
            </button>
          </div>
        )}

        <CarouselContainer carouselRef={carouselRef} className='mb-[unset]!'>
          {testimonials?.map((testimonial: any, index: number) => {
            const isActive = index === activeIndex;

            return (
              <div
                key={index}
                ref={handleAddCarouselItemsToRef}
                className='flex min-w-full flex-col justify-center py-[4rem] not-last:mr-[25%] md:py-[8.3rem] lg:px-[4.5rem] lg:py-[8rem]'
              >
                <div ref={isActive ? quoteWrapRef : null} className='relative mx-auto w-full'>
                  {/* Desktop start quote */}
                  <svg
                    className='pointer-events-none absolute hidden lg:block'
                    style={{
                      left: startQuotePos.left,
                      top: startQuotePos.top,
                      visibility: isActive && startQuotePos.ready ? 'visible' : 'hidden',
                    }}
                    xmlns='http://www.w3.org/2000/svg'
                    width='69'
                    height='52'
                    viewBox='0 0 69 52'
                    fill='none'
                  >
                    <path
                      d='M28.11 4.24425C24.59 5.92425 14.69 12.0442 12.27 23.5342H23.44C25.72 23.5342 27.57 25.3842 27.57 27.6642V46.9642C27.57 49.2442 25.72 51.0942 23.44 51.0942H4.13C1.85 51.0942 0 49.2442 0 46.9642V34.3242C0.27 10.8442 19.83 2.33425 26.45 0.114246C27.63 -0.275754 28.9 0.364246 29.26 1.55425V1.58425C29.6 2.63425 29.1 3.77425 28.1 4.24425H28.11Z'
                      fill='url(#paint0_linear_17488_4687)'
                    />
                    <path
                      d='M66.9 4.24425C63.38 5.92425 53.48 12.0442 51.06 23.5342H62.23C64.51 23.5342 66.36 25.3842 66.36 27.6642V46.9642C66.36 49.2442 64.51 51.0942 62.23 51.0942H42.92C40.64 51.0942 38.79 49.2442 38.79 46.9642V34.3242C39.06 10.8442 58.62 2.33425 65.24 0.114246C66.42 -0.275754 67.69 0.364246 68.05 1.55425V1.58425C68.39 2.63425 67.89 3.77425 66.89 4.24425H66.9Z'
                      fill='url(#paint1_linear_17488_4687)'
                    />
                    <defs>
                      <linearGradient
                        id='paint0_linear_17488_4687'
                        x1='0'
                        y1='25.5542'
                        x2='29.37'
                        y2='25.5542'
                        gradientUnits='userSpaceOnUse'
                      >
                        <stop stopColor='#E2E5E6' stopOpacity='0.44' />
                        <stop offset='1' stopColor='#DCDFE1' stopOpacity='0.5' />
                      </linearGradient>
                      <linearGradient
                        id='paint1_linear_17488_4687'
                        x1='38.79'
                        y1='0.00424605'
                        x2='68.16'
                        y2='0.00424605'
                        gradientUnits='userSpaceOnUse'
                      >
                        <stop stopColor='#E2E5E6' stopOpacity='0.44' />
                        <stop offset='1' stopColor='#DCDFE1' stopOpacity='0.5' />
                      </linearGradient>
                    </defs>
                  </svg>

                  {/* Mobile / Tablet start quote */}
                  <span
                    className='text-body-copy--dark pointer-events-none absolute text-[2rem] leading-[2.4rem] md:text-[2.7rem] md:leading-[4.2rem] lg:hidden'
                    style={{
                      left: startQuotePos.left,
                      top: startQuotePos.top,
                      visibility: isActive && startQuotePos.ready ? 'visible' : 'hidden',
                    }}
                  >
                    “
                  </span>

                  {/* Tight measurement/selectable wrapper so the selectable zone only hugs the text */}
                  <div className='flex justify-center'>
                    <div
                      ref={isActive ? bodyCopyRootRef : null}
                      className={
                        isDesktopViewport
                          ? 'inline-block max-w-full cursor-text select-text'
                          : 'inline-block max-w-full'
                      }
                      style={desktopSelectableStyle}
                      {...desktopInteractionProps}
                    >
                      <RenderBodyCopy
                        className='text-body-copy--dark 3xl:max-w-[85%] 3xl:[&>p]:px-[0] mx-auto mb-[0] text-center text-[2rem] leading-[2.4rem] lg:mx-auto lg:max-w-[93%] 2xl:max-w-[98%] 2xl:px-[0] [&>p]:mb-0 md:[&>p]:text-[2.7rem] md:[&>p]:leading-[4.2rem]'
                        entryId={testimonial?.sys?.id}
                        bodyCopy={testimonial?.bodyCopy?.json?.content}
                      />
                    </div>
                  </div>

                  {/* Desktop end quote */}
                  <svg
                    className='pointer-events-none absolute hidden lg:block'
                    style={{
                      left: endQuotePos.left,
                      top: endQuotePos.top,
                      visibility: isActive && endQuotePos.ready ? 'visible' : 'hidden',
                    }}
                    xmlns='http://www.w3.org/2000/svg'
                    width='69'
                    height='52'
                    viewBox='0 0 69 52'
                    fill='none'
                  >
                    <path
                      d='M40.0463 46.85C43.5663 45.17 53.4663 39.05 55.8863 27.56H44.7163C42.4363 27.56 40.5863 25.71 40.5863 23.43V4.13C40.5863 1.85 42.4363 0 44.7163 0H64.0263C66.3063 0 68.1563 1.85 68.1563 4.13V16.77C67.8863 40.25 48.3363 48.76 41.7063 50.97C40.5263 51.36 39.2563 50.72 38.8963 49.53V49.5C38.5563 48.45 39.0563 47.31 40.0563 46.84L40.0463 46.85Z'
                      fill='url(#paint0_linear_17488_4690)'
                    />
                    <path
                      d='M1.25622 46.85C4.77622 45.17 14.6762 39.05 17.0962 27.56H5.92622C3.64622 27.56 1.79622 25.71 1.79622 23.43V4.13C1.79622 1.85 3.64622 0 5.92622 0H25.2362C27.5162 0 29.3662 1.85 29.3662 4.13V16.77C29.0962 40.25 9.54622 48.76 2.91622 50.97C1.73622 51.36 0.466221 50.72 0.106221 49.53V49.5C-0.233779 48.45 0.266221 47.31 1.26622 46.84L1.25622 46.85Z'
                      fill='url(#paint1_linear_17488_4690)'
                    />
                    <defs>
                      <linearGradient
                        id='paint0_linear_17488_4690'
                        x1='68.1463'
                        y1='25.54'
                        x2='38.7763'
                        y2='25.54'
                        gradientUnits='userSpaceOnUse'
                      >
                        <stop stopColor='#E2E5E6' stopOpacity='0.44' />
                        <stop offset='1' stopColor='#DCDFE1' stopOpacity='0.5' />
                      </linearGradient>
                      <linearGradient
                        id='paint1_linear_17488_4690'
                        x1='29.3562'
                        y1='25.54'
                        x2='-0.0137789'
                        y2='25.54'
                        gradientUnits='userSpaceOnUse'
                      >
                        <stop stopColor='#E2E5E6' stopOpacity='0.44' />
                        <stop offset='1' stopColor='#DCDFE1' stopOpacity='0.5' />
                      </linearGradient>
                    </defs>
                  </svg>

                  {/* Mobile / Tablet end quote */}
                  <span
                    className='text-body-copy--dark pointer-events-none absolute text-[2rem] leading-[2.4rem] md:text-[2.7rem] md:leading-[4.2rem] lg:hidden'
                    style={{
                      left: endQuotePos.left,
                      top: endQuotePos.top,
                      visibility: isActive && endQuotePos.ready ? 'visible' : 'hidden',
                    }}
                  >
                    ”
                  </span>
                </div>

                <div className='mx-auto mt-[4.2rem] flex w-full max-w-[35rem] items-center gap-[1rem] sm:mt-[3.2rem] sm:w-full sm:max-w-[50rem] sm:flex-row sm:items-center sm:gap-[0] lg:mt-[4rem] lg:max-w-[77.9rem]'>
                  <div className='mt-[2rem] mb-[2.4rem] hidden w-[10%] border-b-[0.1rem] border-b-[rgba(0,0,0,0.2)] sm:relative sm:top-[-0.5rem] sm:mr-[1rem] sm:block sm:w-[10%] md:mr-[1.8rem]' />

                  {testimonial?.profileImage?.url && (
                    <div className='relative flex justify-center sm:top-[-0.6rem] sm:mb-[0] sm:w-[40%] md:w-[17.437rem]'>
                      <NextImage
                        className='w-[12.1rem] sm:mb-[unset] md:w-2xs lg:relative lg:z-[-5] lg:block'
                        entryId={testimonial?.sys?.id}
                        fieldId='profileImage'
                        src={testimonial?.profileImage?.url}
                        alt={testimonial?.profileImage?.description}
                        objectFit='contain'
                      />
                    </div>
                  )}

                  <div className='w-full'>
                    <InspectorWrapper entryId={testimonial?.sys?.id} fieldId='name'>
                      <span
                        className={`text-primary relative z-2 mb-[1.3rem] text-[1.7rem] leading-none font-medium sm:pl-[1rem] sm:text-left md:text-[2.8rem] md:leading-none lg:max-w-[45rem] lg:pl-[1.8rem] ${
                          isDesktopViewport ? 'cursor-text select-text' : ''
                        }`}
                        style={desktopSelectableStyle}
                        {...desktopInteractionProps}
                      >
                        {testimonial?.name}
                      </span>
                    </InspectorWrapper>

                    <div
                      className='ms:w-[100%] relative z-2 mt-[1.1rem] mb-[1.4rem] ml-[0] w-[90%] border-b-[0.1rem] border-b-[rgba(0,0,0,0.2)] sm:mx-[unset] sm:ml-[1rem] md:mt-[2rem] md:mb-[2rem] lg:mx-auto lg:ml-[1.8rem]'
                      aria-hidden='true'
                    />

                    <InspectorWrapper entryId={testimonial?.sys?.id} fieldId='title'>
                      <span
                        className={`text-body-copy--dark block text-[1.5rem] sm:pl-[1rem] sm:text-left md:mb-[1.2rem] md:text-[1.6rem] lg:pl-[1.8rem] lg:leading-none ${
                          isDesktopViewport ? 'cursor-text select-text' : ''
                        }`}
                        style={desktopSelectableStyle}
                        {...desktopInteractionProps}
                      >
                        {testimonial?.title}
                      </span>
                    </InspectorWrapper>

                    <InspectorWrapper entryId={testimonial?.sys?.id} fieldId='company'>
                      <span
                        className={`text-body-copy--dark block text-[1.5rem] font-medium sm:pl-[1rem] sm:text-left md:text-[1.6rem] md:leading-none lg:pl-[1.8rem] ${
                          isDesktopViewport ? 'cursor-text select-text' : ''
                        }`}
                        style={desktopSelectableStyle}
                        {...desktopInteractionProps}
                      >
                        {testimonial?.company}
                      </span>
                    </InspectorWrapper>
                  </div>
                </div>
              </div>
            );
          })}
        </CarouselContainer>

        <CarouselIndicators
          className='relative z-10 mb-[4rem] flex justify-center'
          maxIndex={maxIndex}
          handleJumpToSlide={handleJumpToSlide}
          handleAddIndicatorRef={handleAddIndicatorRef}
          activeIndex={activeIndex}
        />
      </Container>
    </>
  );
};

export default Testimonials;
